import { useComputedColorScheme } from "@mantine/core";
import { useMemo } from "react";

import { EChart, type EChartOption } from "@/viz/EChart";
import { VIZ_FONT, mantineColor, vizTokens } from "@/viz/tokens";

interface Threshold {
  min: number;
  color: string;
}

interface Props {
  keys: string[];
  values: (number | null)[];
  /** Names the single series, so no legend box is needed. */
  valueLabel: string;
  formatValue?: (v: number) => string;
  /**
   * Per-bar severity flags: a value greater than `min` gets `color`, picking
   * the highest-`min` rung it clears. Empty (the default) leaves every bar
   * the single series color.
   */
  thresholds?: Threshold[];
}

const ROW_HEIGHT = 30;
const CHROME_HEIGHT = 44; // axis band + padding — the container must include it

/**
 * Horizontal bar chart for ranked categorical magnitude.
 *
 * Horizontal rather than vertical because the categories are text: vertical
 * bars would need rotated or truncated tick labels once names get long, and a
 * clipped label is worse than a longer chart.
 *
 * One series, so every bar is the same color by default. Coloring bars by
 * category would double-encode length as hue — spending the only free channel
 * on information the bar length already carries — and a value-ramp on nominal
 * categories is a documented anti-pattern. `thresholds` is a deliberate,
 * narrow exception: a small number of discrete severity rungs (config.
 * ColorThreshold), not a continuous ramp, and it flags values crossing an
 * operational line rather than restating the length that is already visible.
 */
export function BarChart({ keys, values, valueLabel, formatValue, thresholds = [] }: Props) {
  const scheme = useComputedColorScheme("light", { getInitialValueInEffect: true });

  const option = useMemo<EChartOption>(() => {
    const t = vizTokens(scheme);
    const fmt = formatValue ?? ((v: number) => v.toLocaleString());

    // The rung with the highest `min` that `v` clears wins, so a value past
    // both a red and an orange threshold reads as red, not whichever the
    // file happened to list first.
    const colorFor = (v: number | null): string => {
      if (v == null) return t.series1;
      let chosen = t.series1;
      let bestMin = -Infinity;
      for (const { min, color } of thresholds) {
        if (v > min && min > bestMin) {
          chosen = mantineColor(color, scheme);
          bestMin = min;
        }
      }
      return chosen;
    };

    // ECharts draws the first category at the bottom of a value-y axis, so the
    // arrays are reversed to put the largest bar on top.
    const cats = [...keys].reverse();
    const revValues = [...values].reverse();
    const vals =
      thresholds.length > 0
        ? revValues.map((v) => ({ value: v, itemStyle: { color: colorFor(v) } }))
        : revValues;

    return {
      backgroundColor: "transparent",
      animationDuration: 260,
      grid: { left: 4, right: 56, top: 8, bottom: 4, containLabel: true },
      tooltip: {
        trigger: "item",
        backgroundColor: t.tooltipBg,
        borderColor: t.tooltipBorder,
        borderWidth: 1,
        padding: [8, 12],
        textStyle: { color: t.textPrimary, fontFamily: VIZ_FONT, fontSize: 13 },
        extraCssText: "border-radius:8px; box-shadow:0 4px 16px rgba(0,0,0,0.12);",
        formatter: (p: { name: string; value: number | null }) =>
          `<strong>${escapeHtml(p.name)}</strong><br/>` +
          `<span style="color:${t.textSecondary}">${escapeHtml(valueLabel)}:</span> ` +
          `${p.value == null ? "—" : fmt(p.value)}`,
      },
      xAxis: {
        type: "value",
        // Hairline, solid. Dashed gridlines read as "threshold" when they are
        // only a grid.
        splitLine: { lineStyle: { color: t.gridline, width: 1, type: "solid" } },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: t.muted,
          fontFamily: VIZ_FONT,
          fontSize: 12,
          // Aligned columns of digits, so equal-width figures are correct here.
          fontWeight: "normal",
          formatter: (v: number) => fmt(v),
        },
      },
      yAxis: {
        type: "category",
        data: cats,
        axisLine: { lineStyle: { color: t.axis, width: 1 } },
        axisTick: { show: false },
        axisLabel: { color: t.textSecondary, fontFamily: VIZ_FONT, fontSize: 13 },
      },
      series: [
        {
          type: "bar",
          name: valueLabel,
          data: vals,
          // Thin marks with generous separation, not thick saturated blocks.
          barWidth: 14,
          itemStyle: {
            color: t.series1,
            // Rounded only on the data end; the baseline end stays square so
            // bars remain anchored to the axis.
            borderRadius: [0, 4, 4, 0],
          },
          emphasis: { itemStyle: { color: t.series1Hover } },
          label: {
            // Selective direct labels: outside the bar end, so they cannot be
            // clipped by a short bar.
            show: true,
            position: "right",
            distance: 8,
            color: t.textSecondary,
            fontFamily: VIZ_FONT,
            fontSize: 12,
            formatter: (p: { value: number | null }) =>
              p.value == null ? "—" : fmt(p.value),
          },
        },
      ],
    } as EChartOption;
  }, [keys, values, valueLabel, formatValue, scheme, thresholds]);

  return (
    <EChart
      option={option}
      height={keys.length * ROW_HEIGHT + CHROME_HEIGHT}
      ariaLabel={`Bar chart: ${valueLabel} by category, ${keys.length} categories. The table view lists every value.`}
    />
  );
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
